<template>
  <div class="self-report-panel">
    <div class="report-body">
      <div class="report-list" :class="{ 'report-list--empty': !selfReportedFeedbacks.length }">
        <!-- 内层容器高度随内容增长，让时间轴虚线跟随内容整体滚动 -->
        <div class="report-list-inner">
          <div v-for="(item, i) in renderList" :key="item.id || i" class="report-row">
            <span class="dot" />
            <a-collapse
              class="row-collapse"
              :bordered="false"
              :active-key="activeKeys.includes(panelKey(item)) ? [panelKey(item)] : []"
              @change="keys => onToggleExpand(panelKey(item), keys)"
            >
              <a-collapse-panel :key="panelKey(item)" :show-arrow="false">
                <template #header>
                  <div class="panel-header">
                    <div class="info">
                      <div class="name-row">
                        <div class="name">{{ item.title }}</div>
                        <div v-if="item.sourceLabel" class="source-label">
                          {{ item.sourceLabel }}
                        </div>
                      </div>
                      <div class="date">{{ item.feedbackDate }}</div>
                    </div>
                    <div
                      class="status"
                      :style="{
                        color: getSelfReportStatusColor(item.status),
                        backgroundColor: getSelfReportStatusBackgroundColor(item.status)
                      }"
                    >
                      {{ item.statusLabel }}
                    </div>
                    <span
                      class="arrow"
                      :class="{ 'arrow--expanded': activeKeys.includes(panelKey(item)) }"
                    />
                  </div>
                </template>
                <div class="report-content">
                  <p class="content-paragraph content-paragraph-bold">患者自述原文：</p>
                  <p class="content-paragraph" v-for="(paragraph, i) in item.contentList" :key="i">
                    {{ paragraph }}
                  </p>
                </div>
              </a-collapse-panel>
            </a-collapse>
          </div>

          <MoreButton v-if="showMoreButton" @expand="expanded = true" />
        </div>
        <a-empty v-if="!selfReportedFeedbacks.length" :image="emptyImg" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, ref, watch } from 'vue'
import emptyImg from '@/assets/img/empty.png'
import MoreButton from './MoreButton.vue'
import { useExpandableList } from '../../hooks/useExpandableList'
import {
  createSelfReportFeedbacks,
  getSelfReportStatusColor,
  getSelfReportStatusBackgroundColor
} from '../../model/selfReport'

const props = defineProps({
  selfReportedFeedbacks: {
    type: Array,
    default: () => []
  }
})

const selfReportedFeedbacks = computed(() => createSelfReportFeedbacks(props.selfReportedFeedbacks))

const DEFAULT_VISIBLE_COUNT = 4

const { expanded, displayList, showMoreButton, resetExpand } = useExpandableList(
  selfReportedFeedbacks,
  DEFAULT_VISIBLE_COUNT
)

const renderList = computed(() => {
  return displayList.value.map(item => {
    const contentList = item.content.split('；').map(i => `“${i}”`)
    return {
      ...item,
      contentList
    }
  })
})

// Collapse 展开状态统一收敛到 activeKeys，便于箭头态与内容联动
const activeKeys = ref([])

// 数据源变化（切换患者/刷新完成）时重置展开态，避免旧患者的展开项残留
watch(selfReportedFeedbacks, () => {
  resetExpand()
  activeKeys.value = []
})

// vc-collapse 内部以 String(key) 匹配面板且 change 回传字符串数组，
// 统一转字符串，避免数字 recordId 与字符串 key 类型不一致导致无法展开
const panelKey = item => String(item.recordId)

const onToggleExpand = (key, keys) => {
  const isOpen = Array.isArray(keys) ? keys.includes(key) : keys === key
  activeKeys.value = isOpen
    ? [...new Set([...activeKeys.value, key])]
    : activeKeys.value.filter(item => item !== key)
}
</script>

<style lang="scss" scoped>
.self-report-panel {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.report-body {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.loading-mask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
  z-index: 3;
}

.report-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  // 预留滚动条 gutter：Collapse 展开/收起时滚动条出现不再挤压内容宽度，避免布局抖动
  // （旧 Safari 不支持时回退为现状行为，不会更差）
  scrollbar-gutter: stable;
  padding: 4px 4px 0 0;

  &.report-list--empty {
    display: flex;
    align-items: center;
    justify-content: center;

    .report-list-inner::before {
      display: none;
    }
  }
}

// 内层容器随内容撑高：绝对定位的虚线以其为包含块，才能铺满全部内容并跟随滚动
.report-list-inner {
  position: relative;

  // 左侧时间轴虚线（2px 段 / 6px 间隔）
  &::before {
    content: '';
    position: absolute;
    left: 5px;
    top: 24px;
    bottom: 24px;
    width: 3px;
    background-image: repeating-linear-gradient(
      to bottom,
      #d3e2ff 0,
      #d3e2ff 2px,
      transparent 2px,
      transparent 8px
    );
  }
}

.report-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}

.dot {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #aec9ff;
  border: 3px solid #ffffff;
  box-sizing: border-box;
}

.row-collapse {
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;

  :deep(.ant-collapse-item) {
    border: none;
    background: #f8fafc;
    border-radius: 8px;
    overflow: hidden;

    .ant-collapse-header {
      // 覆盖 antdv 为箭头预留的 padding-left（showArrow=false 后不再需要）
      padding: 10px 16px !important;
    }

    .ant-collapse-content {
      border-top: none;
      background: transparent;

      .ant-collapse-content-box {
        padding: 0 16px 10px;
      }
    }
  }
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  .info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .name-row {
      display: flex;
      align-items: center;
      gap: 4px;
      min-width: 0;
    }

    .name {
      color: #092954;
      font-size: 15px;
      font-weight: 500;
      line-height: 21px;
    }

    .source-label {
      flex-shrink: 0;
      padding: 1px 4px;
      border: 1px solid #ced9f0;
      border-radius: 4px;
      color: #566f8d;
      font-size: 12px;
      font-weight: 400;
      line-height: 14px;
    }

    .date {
      color: #96a5be;
      font-size: 14px;
      line-height: 20px;
    }
  }

  .status {
    flex-shrink: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    padding: 4px 8px;
    border-radius: 6px;
  }
}

.arrow {
  position: relative;
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  transition: transform 0.2s;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 5px;
    width: 7px;
    border-top: 1.5px solid #566f8d;
  }

  &::before {
    left: 0;
    transform: rotate(45deg);
  }

  &::after {
    right: 0;
    transform: rotate(-45deg);
  }

  &--expanded {
    transform: rotate(180deg);
  }
}

.report-content {
  background: #ffffff;
  border-radius: 4px;
  padding: 4px;
  line-height: 20px;
  font-size: 14px;
  color: #3f5b7d;
  font-weight: 400;
}

.content-paragraph {
  white-space: pre-wrap;
  word-break: break-all;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.content-paragraph-bold {
  font-weight: 500;
  margin-bottom: 10px;
}
</style>
